import React, { useState, useEffect } from "react";
import axios from "axios";
import { message, Alert } from "antd";
import ModalItem from "../../../../Components/ModalItem";
import { InputNumberItem, TextAreaItem } from "../../../../Components/Items";
import SelectUbicacionesProyectoItem from "../../../../Components/SelectUbicacionesProyectoItem";

//Modal para mover una cantidad de un insumo de una ubicación a otra dentro del mismo almacén.
//No genera movimientos de salida/entrada: el backend reasigna directamente el ubicacionId de los
//registros de entrada existentes (ver moverUbicacionInsumo en Utils/inventario.utils.js).
const ModalMoverUbicacion = ({
  visible,
  onClose,
  onSuccess,
  empresaId,
  headersToken,
  proyectoId,
  insumoId,
  insumoNombre,
  ubicacionOrigen,
  catalogoUbicaciones,
  ubicacionesConExistencia,
}) => {
  const [cantidad, setCantidad] = useState(undefined);
  const [ubicacionDestinoId, setUbicacionDestinoId] = useState(undefined);
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (visible) {
      setCantidad(undefined);
      setUbicacionDestinoId(undefined);
      setObservaciones("");
    }
  }, [visible, ubicacionOrigen]);

  const destinoCatalogo = (catalogoUbicaciones || []).find(
    (valorUbicacion) => String(valorUbicacion.ubicacionId) === String(ubicacionDestinoId)
  );
  const existenciaDestino = (ubicacionesConExistencia || []).find(
    (valorUbicacion) => String(valorUbicacion.ubicacionId) === String(ubicacionDestinoId)
  )?.cantidad || 0;
  const cantidadMaximaDestino = destinoCatalogo?.cantidadMaxima;
  const excedeCapacidad =
    (cantidadMaximaDestino || cantidadMaximaDestino === 0) &&
    cantidad &&
    existenciaDestino + Number(cantidad) > cantidadMaximaDestino;

  const esSinAsignar = !ubicacionOrigen?.ubicacionId;

  const mismaUbicacion =
    !esSinAsignar &&
    ubicacionDestinoId &&
    ubicacionOrigen &&
    String(ubicacionDestinoId) === String(ubicacionOrigen.ubicacionId);

  const puedeGuardar =
    !!ubicacionDestinoId &&
    !mismaUbicacion &&
    !!cantidad &&
    Number(cantidad) > 0 &&
    Number(cantidad) <= (ubicacionOrigen?.cantidad || 0);

  const guardar = async () => {
    if (!puedeGuardar) {
      message.warning("Captura una cantidad válida y una ubicación destino diferente a la de origen");
      return;
    }
    setGuardando(true);
    try {
      const res = await axios.post(
        esSinAsignar ? "cambioUbicacionInsumo/asignarUbicacionSinAsignar" : "cambioUbicacionInsumo/moverUbicacionInsumo",
        {
          empresaId,
          proyectoId,
          insumoId,
          ...(esSinAsignar ? {} : { ubicacionOrigenId: ubicacionOrigen.ubicacionId }),
          ubicacionDestinoId,
          cantidad: Number(cantidad),
          observaciones,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success(esSinAsignar ? "Ubicación asignada correctamente" : "Ubicación actualizada correctamente");
        onSuccess();
      } else {
        message.error(res.data.error || "No se pudo actualizar la ubicación");
      }
    } catch (error) {
      console.error("Error al actualizar la ubicación:", error);
      message.error("No se pudo actualizar la ubicación");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <ModalItem
      title={`${esSinAsignar ? "Asignar" : "Mover"} ubicación — ${insumoNombre || ""}`}
      visible={visible}
      onCancel={onClose}
      onOk={guardar}
      okText={esSinAsignar ? "Asignar" : "Mover"}
      cancelText="Cancelar"
      confirmLoading={guardando}
      spinning={guardando}
      footer={true}
      width={520}
    >
      {ubicacionOrigen && (
        <>
          <div style={{ marginBottom: 12 }}>
            <span style={{ color: "#6b7280" }}>Origen: </span>
            <strong>
              {ubicacionOrigen.clave} {ubicacionOrigen.nombre ? `- ${ubicacionOrigen.nombre}` : ""}
            </strong>
            <span style={{ marginLeft: 12, color: "#6b7280" }}>Disponible: </span>
            <strong>{Number(ubicacionOrigen.cantidad || 0).toFixed(4)}</strong>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: "#6b7280" }}>Cantidad a mover</div>
            <InputNumberItem
              style={{ width: "100%" }}
              min={0}
              max={ubicacionOrigen.cantidad}
              value={cantidad}
              onChange={(value) => setCantidad(value)}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: "#6b7280" }}>Ubicación destino</div>
            <SelectUbicacionesProyectoItem
              empresaId={empresaId}
              headersToken={headersToken}
              proyectoId={proyectoId}
              value={ubicacionDestinoId}
              onChange={(value) => setUbicacionDestinoId(value)}
            />
            {mismaUbicacion && (
              <Alert style={{ marginTop: 8 }} type="error" showIcon message="La ubicación destino debe ser diferente a la de origen" />
            )}
            {!mismaUbicacion && excedeCapacidad && (
              <Alert
                style={{ marginTop: 8 }}
                type="warning"
                showIcon
                message={`Esta ubicación tiene capacidad máxima de ${cantidadMaximaDestino}; con este movimiento quedaría en ${(
                  existenciaDestino + Number(cantidad)
                ).toFixed(4)}`}
              />
            )}
          </div>

          <div>
            <div style={{ marginBottom: 4, color: "#6b7280" }}>Observaciones (opcional)</div>
            <TextAreaItem rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ModalMoverUbicacion;
